import { X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { IdDisplay } from '@components/id-display'
import { getInvoiceScorecardEmails, getInvoiceScore, saveInvoiceScore } from '../../api/api.js'
import { useLoadingStore, useUserStore } from '../../store'
import { scoreValidations } from '../../utils/utils.js'

import { AdasBox, AdasButton, AdasModal, AdasPaper, AdasTextField, AdasTypography } from '@components/wrapper-components'
import { ScoreEmail } from '@components/score-email/ScoreEmail.js'
import { Logger } from '../../logger'

const ScoreRow = ({ id, title, target = '10', value, handleChange, error, readOnly }) => {
  return (
    <AdasBox id={id + '_row'} className='flex items-center justify-between w-full mb-4 ml-2'>
      {/* Title on the left */}
      <AdasTypography className='flex-1'>{title}</AdasTypography>

      {/* Input or Value in the center, right-aligned */}
      {readOnly ? (
        <AdasTypography className='text-center mr-4 ml-auto w-12 h-[28px]'>{value}</AdasTypography>
      ) : (
        <AdasTextField
          id={id}
          name={id}
          value={value}
          onChange={handleChange}
          error={error}
          size='small'
          className='text-center mr-4 ml-auto w-12 h-[28px]'
          inputProps={{
            style: {
              padding: '0 10px',
              width: '48px',
              height: '28px',
              textAlign: 'center'
            },
            maxLength: 3
          }}
        />
      )}

      {/* Target score on the right */}
      <AdasTypography className='text-center min-w-12'>{target}</AdasTypography>
    </AdasBox>
  )
}

const defaultScore = {
  preCalDocScore: ' ',
  preCalPhotoScore: ' ',
  prodCalScore: ' ',
  prodCalSrvCustScore: ' ',
  calDocScore: ' ',
  calPhotoScore: ' ',
  postDocScore: ' ',
  postPhotoScore: ' '
}

const defaultTouched = {
  notes: false,
  preCalDocScore: false,
  preCalPhotoScore: false,
  prodCalScore: false,
  prodCalSrvCustScore: false,
  calDocScore: false,
  calPhotoScore: false,
  postDocScore: false,
  postPhotoScore: false
}

export const ScoreDrawer = ({ invoiceId, referenceNumber, roNumber, isOpen, onClose, setIsScored }) => {
  const [score, setScore] = useState(defaultScore)
  const [auditorName, setAuditorName] = useState(null)
  const [auditDate, setAuditDate] = useState(null)
  const [auditCompleted, setAuditCompleted] = useState(false)
  const [notes, setNotes] = useState('')
  const [touched, setTouched] = useState(defaultTouched)
  const [emailContacts, setEmailContacts] = useState({})
  const [valid, setValid] = useState({
    preCalDocScore: scoreValidations.preCalDocScore(score?.preCalDocScore),
    preCalPhotoScore: scoreValidations.preCalPhotoScore(score?.preCalPhotoScore),
    prodCalScore: scoreValidations.prodCalScore(score?.prodCalScore),
    prodCalSrvCustScore: scoreValidations.prodCalSrvCustScore(score?.prodCalSrvCustScore),
    calDocScore: scoreValidations.calDocScore(score?.calDocScore),
    calPhotoScore: scoreValidations.calPhotoScore(score?.calPhotoScore),
    postDocScore: scoreValidations.postDocScore(score?.postDocScore),
    postPhotoScore: scoreValidations.postPhotoScore(score?.postPhotoScore)
  })
  const currentUser = useUserStore((store) => store.currentUser)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const MAX_NOTE_CHARACTER = 300
  const everyItemValid = useMemo(() => {
    let allValid = true
    for (const value of Object.values(valid)) {
      if (!value) allValid = false
    }
    return allValid
  }, [valid])

  const someFieldTouched = useMemo(() => {
    let oneTouched = false
    for (const value of Object.values(touched)) {
      if (value) oneTouched = true
    }
    return oneTouched
  }, [touched])

  const scoreTotal = useMemo(() => {
    let total = 0
    for (const [key, value] of Object.entries(score)) {
      if (key !== 'notes' && value !== ' ') {
        total = total + parseInt(value)
      }
    }
    return total
  }, [score])

  const updateScoreFormData = useCallback((name, value) => {
    setScore((prev) => {
      const contact = {
        ...prev,
        [name]: value
      }
      return contact
    })
    setValid((prev) => {
      const valid = {
        ...prev
      }
      if (scoreValidations[name]) {
        valid[name] = scoreValidations[name](value)
      }
      return valid
    })
    setTouched((prev) => {
      return {
        ...prev,
        [name]: true
      }
    })
  }, [])

  const handleNoteChange = useCallback((e) => {
    setNotes(e.target.value)
    setTouched((prev) => {
      return {
        ...prev,
        notes: true
      }
    })
  }, [])

  const handleInputChange = useCallback(
    (e) => {
      let value = e.target.value
      value = value.replace(/\D/g, '')
      value = value === '' ? ' ' : value
      const name = e.target.name
      updateScoreFormData(name, value)
    },
    [updateScoreFormData]
  )

  const closeDrawer = useCallback(
    (saving = false, completed = false) => {
      if (saving) {
        const scoreSaveResponse = saveInvoiceScore({
          invoiceId: invoiceId,
          data: {
            ...score,
            notes: notes,
            completed: completed
          }
        })
        scoreSaveResponse.then((response) => {
          if (response.status === 200) {
            setIsScored(completed)
            setAuditCompleted(completed)
            if (!completed) {
              onClose()
            }
          } else {
            console.log('unable to save invoice score')
          }
        })
      } else {
        onClose()
      }
    },
    [invoiceId, notes, onClose, score, setIsScored]
  )
  const emailInvoiceHandler = useCallback(async () => {
    if (currentUser?.is_admin) {
      const params = {
        invoiceId
      }

      setLoading(true)
      try {
        const emailContactResponse = await getInvoiceScorecardEmails(params)
        if (emailContactResponse.status === 200) {
          setEmailContacts(emailContactResponse.data)
        }
      } catch (error) {
        Logger.error({ message: error, payload: { invoiceId, path: 'InvoiceSummaryPage/emailInvoiceHandler' } })
      } finally {
        setLoading(false)
      }
    }
  }, [invoiceId])

  useEffect(() => {
    if (isOpen && invoiceId) {
      const scoreResponse = getInvoiceScore({
        invoiceId: invoiceId
      })
      scoreResponse
        .then((response) => {
          if (response.status === 200 && response.data) {
            emailInvoiceHandler()
            setAuditDate(response.data.updated_at)
            setAuditorName(response.data?.user?.name)
            setNotes(response.data.notes)
            setAuditCompleted(response.data.is_completed)
            if (response.data.items) {
              for (const scoreItem of Object.values(response.data.items)) {
                updateScoreFormData(scoreItem.category, scoreItem.score + '')
              }
            } else {
              setScore(defaultScore)
              setTouched(defaultTouched)
            }
          }
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, [invoiceId, isOpen, updateScoreFormData])

  return (
    <AdasModal open={isOpen} onClose={() => closeDrawer()} className='w-[640px] max-w-[92vw] max-h-[90vh] overflow-y-auto'>
      <AdasBox className='py-[25px] px-6'>
        <AdasBox className='flex items-center justify-between mb-6'>
          <AdasTypography component='span' variant='h6' className='mb-[.3rem]'>
            Quality Assurance Scorecard
          </AdasTypography>
          <AdasButton buttonType='iconButton' className='text-info cursor-pointer p-0.5' onClick={() => closeDrawer()}>
            <X className='w-4 h-4' />
          </AdasButton>
        </AdasBox>
        <AdasBox className='mb-4 mt-[-1.5rem] text-[grey]'>
          <IdDisplay itemId={referenceNumber} roNumber={roNumber} itemType='Invoice' />
        </AdasBox>
        <AdasBox className='flex w-full' my={3}>
          <AdasTypography className='text-sm font-semibold leading-[22px] tracking-[0.1px]'>Auditor Name : </AdasTypography>
          <AdasTypography ml={0.5}>{auditorName ? auditorName : currentUser.name}</AdasTypography>
        </AdasBox>
        <AdasBox className='flex w-full' mb={3}>
          <AdasTypography className='text-sm font-semibold leading-[22px] tracking-[0.1px]'>Date Audited : </AdasTypography>
          <AdasTypography ml={0.5}>
            {auditDate ? new Date(auditDate).toLocaleDateString('en-US') : new Date().toLocaleDateString('en-US')}
          </AdasTypography>
        </AdasBox>
        <AdasPaper elevation={1} className='flex flex-wrap w-full p-4 mt-4 bg-[#FDFBFF]'>
          <AdasBox id='headerRow' className='flex justify-between w-full'>
            {/* Empty space for alignment with the title on the left */}
            <AdasBox className='flex-1' />

            {/* Current Score Header */}
            <AdasBox className='mr-4 min-w-[4rem]'>
              <AdasTypography className='text-right'>
                Current
                <br />
                Score
              </AdasTypography>
            </AdasBox>

            {/* Target Score Header */}
            <AdasBox className='text-right min-w-12'>
              <AdasTypography className='text-right'>
                Target <br /> Score
              </AdasTypography>
            </AdasBox>
          </AdasBox>

          <AdasTypography className='mt-0'>PRE CALIBRATION</AdasTypography>
          <ScoreRow
            id='preCalDocScore'
            title='Documentation'
            handleChange={handleInputChange}
            value={score.preCalDocScore}
            error={touched.preCalDocScore && !valid.preCalDocScore}
            readOnly={!currentUser?.is_admin}
          />
          <ScoreRow
            id='preCalPhotoScore'
            title='Photos'
            handleChange={handleInputChange}
            value={score.preCalPhotoScore}
            error={touched.preCalPhotoScore && !valid.preCalPhotoScore}
            readOnly={!currentUser?.is_admin}
          />
          <AdasTypography className='text-xs font-bold leading-[18px] tracking-[1px] text-left w-full pt-2 pb-2 mt-4'>
            PRODUCTS
          </AdasTypography>
          <ScoreRow
            id='prodCalScore'
            title='Calibrations'
            handleChange={handleInputChange}
            value={score.prodCalScore}
            error={touched.prodCalScore && !valid.prodCalScore}
            readOnly={!currentUser?.is_admin}
          />
          <ScoreRow
            id='prodCalSrvCustScore'
            title='Services/Custom'
            handleChange={handleInputChange}
            value={score.prodCalSrvCustScore}
            error={touched.prodCalSrvCustScore && !valid.prodCalSrvCustScore}
            readOnly={!currentUser?.is_admin}
          />
          <AdasTypography className='text-xs font-bold leading-[18px] tracking-[1px] text-left w-full pt-2 pb-2 mt-4'>
            CALIBRATION
          </AdasTypography>
          <ScoreRow
            id='calDocScore'
            title='Documentation'
            target='20'
            handleChange={handleInputChange}
            value={score.calDocScore}
            error={touched.calDocScore && !valid.calDocScore}
            readOnly={!currentUser?.is_admin}
          />
          <ScoreRow
            id='calPhotoScore'
            title='Photos'
            target='20'
            handleChange={handleInputChange}
            value={score.calPhotoScore}
            error={touched.calPhotoScore && !valid.calPhotoScore}
            readOnly={!currentUser?.is_admin}
          />
          <AdasTypography className='text-xs font-bold leading-[18px] tracking-[1px] text-left w-full pt-2 pb-2 mt-4'>
            POST CALIBRATION
          </AdasTypography>
          <ScoreRow
            id='postDocScore'
            title='Documentation'
            handleChange={handleInputChange}
            value={score.postDocScore}
            error={touched.postDocScore && !valid.postDocScore}
            readOnly={!currentUser?.is_admin}
          />
          <ScoreRow
            id='postPhotoScore'
            title='Photos'
            handleChange={handleInputChange}
            value={score.postPhotoScore}
            error={touched.postPhotoScore && !valid.postPhotoScore}
            readOnly={!currentUser?.is_admin}
          />
          <AdasBox id='totalsRow' className='flex w-full items-baseline mb-[.25rem] pl-4 pt-3 mt-3 border-t border-black'>
            <AdasTypography className='font-bold mt-4 pb-2'>TOTAL SCORE</AdasTypography>
            <AdasTypography className='text-center mr-4 ml-auto w-12 h-[28px]'>{scoreTotal}</AdasTypography>
            <AdasTypography className='text-center w-12 h-[28px]'>100</AdasTypography>
          </AdasBox>
        </AdasPaper>
        <AdasTextField
          id='notes'
          name='notes'
          label='Notes'
          multiline
          rows={4}
          value={notes}
          onChange={handleNoteChange}
          placeholder='Type additional notes here...'
          className='w-full mt-8'
          disabled={!currentUser?.is_admin}
          inputProps={{ maxLength: MAX_NOTE_CHARACTER }}
        />
        <AdasBox id='buttonArea' className='flex justify-start flex-row-reverse mt-8'>
          {currentUser?.is_admin ? (
            <AdasButton
              variant='contained'
              aria-label={auditCompleted ? 'Resubmit' : 'Submit'}
              color='primary'
              onClick={() => closeDrawer(true, true)}
              disabled={!(everyItemValid && someFieldTouched)}
            >
              {auditCompleted ? 'Resubmit' : 'Submit'}
            </AdasButton>
          ) : (
            <AdasButton variant='contained' aria-label='Done' color='primary' onClick={() => closeDrawer()}>
              Done
            </AdasButton>
          )}
          {!auditCompleted && currentUser?.is_admin && (
            <AdasButton
              variant='outlined'
              aria-label='Back'
              className='mr-4'
              disabled={!someFieldTouched}
              onClick={() => closeDrawer(true, false)}
            >
              Save
            </AdasButton>
          )}
        </AdasBox>
        {currentUser?.is_admin && auditCompleted && <ScoreEmail contacts={emailContacts} onClose={onClose} invoiceId={invoiceId} />}
      </AdasBox>
    </AdasModal>
  )
}

ScoreDrawer.propTypes = {
  invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  roNumber: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  setIsScored: PropTypes.func
}

ScoreRow.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  target: PropTypes.string,
  value: PropTypes.string,
  handleChange: PropTypes.func,
  error: PropTypes.bool,
  readOnly: PropTypes.bool
}
